import { CameraOutlined } from '@ant-design/icons';
import { Badge } from 'antd';
import { BuyModal } from 'components/Modals/BuyModal';
import ProductCardImage from 'components/UI/ProductCardImage';
import { RealPhotosLegend } from 'components/UI/ProductCardImageSimple.styled';
import { useCompositionButton } from 'hooks/useCompositionButton';
import { useBreakpoints } from 'hooks/useMediaQuery';
import { useShippingFee } from 'hooks/useShippingFee';
import Packing from 'model/Packing';
import ProductOffer from 'model/ProductOffer';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { sessionSelectors } from 'store/state/selectors';
import {
  Card,
  ConceptLabel,
  Divider,
  OfferLabel,
  ProductTitle,
  Section,
} from '../../atoms';
import {
  ActionButtons,
  PriceInfo,
  ProducerInfo,
  QualityInfo,
  ValidityInfo,
} from '../../molecules';
import {
  ProductCardGridContainer,
  ProductImagePlaceholder,
  TitleConceptColumn,
} from './ProductCardGrid.styled';

export interface ProductCardGridProps {
  product: ProductOffer;
  packingSelected?: Packing;
  getLowPrice: number;
  onDetailsClick?: () => void;
  buy: (quantityPacking: number, selectedTypeSale: any) => void | Promise<void>;
  disabled?: boolean;
  showPurchaseActions?: boolean;
  addInCart: (
    quantityPacking: number,
    selectedTypeSale: any,
  ) => void | Promise<void>;
  selectedTypeSale?: any;
  showModalComposition: () => void;
  onSelectPacking?: (id: number) => void;
  isExclusiveOffer?: boolean;
}

function ProductCardGrid({
  product,
  packingSelected,
  getLowPrice,
  onDetailsClick,
  buy,
  disabled = false,
  addInCart,
  selectedTypeSale,
  showModalComposition,
  onSelectPacking,
  isExclusiveOffer = false,
}: ProductCardGridProps) {
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [addToCartModalOpen, setAddToCartModalOpen] = useState(false);

  const { isTabletMiniOrLarger } = useBreakpoints();
  const canShowComposition = useCompositionButton(product);
  const { shippingFee } = useShippingFee(product);
  const isAuthenticated = useSelector(sessionSelectors.getAuthenticated);

  const handleBuyClick = useCallback(() => {
    setBuyModalOpen(true);
  }, []);

  const handleBuyModalClose = useCallback(() => {
    setBuyModalOpen(false);
  }, []);

  const handleAddToCartModalOpen = useCallback(() => {
    setAddToCartModalOpen(true);
  }, []);

  const handleAddToCartModalClose = useCallback(() => {
    setAddToCartModalOpen(false);
  }, []);

  const handleBuyConfirm = useCallback(
    (purchaseData) => {
      setBuyModalOpen(false);
      if (buy) {
        const { quantity, purchaseType } = purchaseData;
        buy(quantity, purchaseType);
      }
    },
    [buy],
  );

  const handleAddToCartConfirm = useCallback(
    (purchaseData) => {
      setAddToCartModalOpen(false);
      if (addInCart) {
        const { quantity, purchaseType } = purchaseData;
        addInCart(quantity, purchaseType);
      }
    },
    [addInCart],
  );

  const handleAddToCart = useCallback(() => {
    if (!addInCart || !selectedTypeSale) {
      return;
    }

    handleAddToCartModalOpen();
  }, [addInCart, selectedTypeSale, handleAddToCartModalOpen]);

  const { offerType } = product;
  const isLkpOffer = useMemo(
    () => offerType === 1 || offerType === 2,
    [offerType],
  );

  const isDirectedOffer = product.offerType === 3;
  const hasSpecialPrice =
    !Number.isNaN(product.specialPrice) && product.specialPrice > 0;
  const hasValidReajustment =
    !Number.isNaN(product.reajustmentAmount) && product.reajustmentAmount !== 0;

  const shouldShowBadge = isDirectedOffer
    ? hasSpecialPrice
    : hasValidReajustment;

  let badgeText = '';
  if (isDirectedOffer && hasSpecialPrice) {
    badgeText = `Oferta direcionada R$${Math.abs(product.specialPrice)
      .toFixed(2)
      .replace('.', ',')}`;
  } else if (product.voucherReajustmentType === 'P') {
    badgeText = `${Math.abs(product.reajustmentAmount)}%`;
  } else {
    badgeText = `R$${Math.abs(product.reajustmentAmount)
      .toFixed(2)
      .replace('.', ',')}`;
  }

  if (!isAuthenticated) {
    return (
      <ProductCardGridContainer className="product-card-grid-container">
        <Card>
          <Section className="product-image-section">
            {product.imagesFormatted?.[0] ? (
              <ProductCardImage
                images={product.imagesFormatted}
                alt={`Imagem de ${product.longName}`}
                productName={product.longName}
                isLkpOffer={isLkpOffer}
                showNavigation
                showLabel={false}
                enableModal
              />
            ) : (
              <ProductImagePlaceholder>Sem imagem</ProductImagePlaceholder>
            )}
          </Section>

          <Divider />

          <Section className="product-card-price">
            <TitleConceptColumn>
              <ProductTitle>{product.longName}</ProductTitle>
              <ConceptLabel conceptName={product.conceptName} reserveSpace />
            </TitleConceptColumn>
          </Section>

          <Divider />

          <Section className="product-quality-producer">
            <ProducerInfo product={product} />
          </Section>
        </Card>
      </ProductCardGridContainer>
    );
  }

  return (
    <ProductCardGridContainer className="product-card-grid-container">
      <Badge.Ribbon
        text={badgeText}
        placement="start"
        color="green"
        style={{
          display: shouldShowBadge ? 'block' : 'none',
          margin: '-15px 0 0 9px',
        }}
      >
        <Card>
          <Section className="offer-header-row">
            <OfferLabel
              offerType={isExclusiveOffer ? 4 : product.offerType}
              upperText
            />
            <ValidityInfo product={product} />
          </Section>

          <Section className="product-image-section">
            {product.imagesFormatted?.[0] ? (
              <ProductCardImage
                images={product.imagesFormatted}
                alt={`Imagem de ${product.longName}`}
                productName={product.longName}
                isLkpOffer={isLkpOffer}
                showNavigation
                showLabel={false}
              />
            ) : (
              <ProductImagePlaceholder>Sem imagem</ProductImagePlaceholder>
            )}
          </Section>

          <Divider />

          <Section className="product-card-price">
            <TitleConceptColumn>
              <ProductTitle>{product.longName}</ProductTitle>
              <ConceptLabel conceptName={product.conceptName} reserveSpace />
            </TitleConceptColumn>

            <PriceInfo
              price={getLowPrice}
              unit="un"
              shipping={shippingFee}
              className="price-info"
            />
          </Section>

          <Divider />

          <Section className="product-quality-producer">
            <QualityInfo
              product={product}
              packingSelected={packingSelected}
              showPacking
              layout="column"
              isMobileGrid={!isTabletMiniOrLarger}
              onSelectPacking={onSelectPacking}
            />
            <ProducerInfo product={product} />
          </Section>

          {isLkpOffer &&
            (Array.isArray(product.gfpImages)
              ? product.gfpImages.length > 0
              : !!product.gfpImages) && (
              <RealPhotosLegend>
                <CameraOutlined />
                Oferta com fotos reais. Veja nos detalhes (GFPs).
              </RealPhotosLegend>
            )}

          <Divider />

          <ActionButtons
            onDetailsClick={onDetailsClick}
            onBuyClick={handleBuyClick}
            onAddToCart={handleAddToCart}
            onCompositionClick={showModalComposition}
            showCompositionButton={canShowComposition}
            disabled={disabled}
            disableBuyButton={!isLkpOffer}
            layout="stacked"
            addToCartText={null}
            detailsTextDesktop={
              isLkpOffer ? 'Detalhes | Comprar por GFP' : 'Detalhes'
            }
            detailsTextMobile={isLkpOffer ? 'Detalhes | GFPs' : 'Detalhes'}
            buyText={isTabletMiniOrLarger ? 'Comprar agora' : 'Comprar'}
          />
        </Card>
      </Badge.Ribbon>
      {buyModalOpen && (
        <BuyModal
          key={packingSelected?.id}
          visible={buyModalOpen}
          onCancel={handleBuyModalClose}
          onConfirm={handleBuyConfirm}
          product={product}
          variant="buy"
          packingSelected={packingSelected}
        />
      )}
      {addToCartModalOpen && (
        <BuyModal
          key={packingSelected?.id}
          visible={addToCartModalOpen}
          onCancel={handleAddToCartModalClose}
          onConfirm={handleAddToCartConfirm}
          product={product}
          variant="addToCart"
          packingSelected={packingSelected}
        />
      )}
    </ProductCardGridContainer>
  );
}

export { ProductCardGrid };
